import { CardActionArea, createStyles, makeStyles, Theme } from '@material-ui/core';
import React from 'react';

import { FeedPostDetailsFragment } from '../../../../graphql/gen/types';
import { colorPalette, spacings, TextSizesEnum } from '../../../../theme';
import { LinesEllipsis } from '../../../shared/primitives/LinesEllipsis';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { plainTextPreview } from '../../common/plainTextPreview';
import { LinkPreviewBox } from '../../shared/LinkPreviewBox';
import { CardLabels } from '../CardLabels';

type Props = {
  plexPost: FeedPostDetailsFragment;
  onSelect: (id: string) => void;
};

export function PlexPostContent(props: Props): React.ReactElement {
  const { plexPost, onSelect } = props;
  const { permalink, title, body, primaryLink, mentions, links } = plexPost;
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <CardActionArea
        className={classes.content}
        onClick={(): void => {
          onSelect(permalink);
        }}
      >
        <div className={classes.contentText}>
          <div className={classes.titleContainer}>
            <PlexTypography className={classes.plexTitle} text={title} size={TextSizesEnum.XLarge} />
            <CardLabels labels={plexPost.labels} />
          </div>

          <PlexTypography className={classes.plexContent} size={TextSizesEnum.Normal}>
            <LinesEllipsis text={plainTextPreview(body, mentions, links)} maxLine={2} />
          </PlexTypography>
        </div>
      </CardActionArea>

      {primaryLink != null && <LinkPreviewBox linkMetadata={primaryLink} />}
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'space-between',
    },
    titleContainer: {
      display: 'inline-block',
    },
    content: {
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'space-between',
      margin: 0,
      padding: '16px 24px',
    },
    contentText: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    plexTitle: {
      color: colorPalette.dark._900,
      fontWeight: 'bold',
      display: 'inline',
    },
    plexContent: {
      marginTop: spacings.small,
      color: colorPalette.purpleGray._500,
      fontWeight: 500,
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap', // fixes bug where only ellipsis would show when text is too long
    },
    media: {
      flexShrink: 0,
      width: 136,
      height: 106,
      marginTop: spacings.xlarge,
      marginLeft: spacings.xlarge,
      marginRight: spacings.xlarge,
      padding: 0,
      justifyContent: 'flex-start',
      borderRadius: spacings.small,
    },
    mediaPicture: {
      width: '100%',
      height: '100%',
      borderRadius: spacings.small,
    },
    mediaLink: {
      border: '2px solid #EAEAEA',
    },
    mediaContent: {
      display: 'flex',
      height: '100%',
      width: '100%',
      flexDirection: 'column',
      justifyContent: 'space-between',
      padding: spacings.small,
    },
    mediaContentTop: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    mediaContentBottom: {
      display: 'flex',
      flexDirection: 'column',
    },
    mediaTitle: {
      color: '#1E1F20',
    },
    mediaSubTitle: {
      color: '#7F85A2',
    },
    linkHref: {
      textDecoration: 'none',
    },
  })
);
